// src/ARMERIX/MANAGEMENT/MANAGEMENTDASHBOARD/CommerceManagementDashboard.tsx

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  Storefront, SupportAgent, EuroSymbol, Book,
  ChevronLeft, ChevronRight,
} from "@mui/icons-material";
import { ShoppingBag } from "lucide-react";

import GeneralCommercePartners    from "../GENERALCOMMERCE/GeneralCommercePartners";
import CommerceManagementSupport  from "../../SUPPORT/CommerceManagementSupport";
import Revenue                    from "../REVENUE/Revenue";
import ElectronicsAcquisition     from "../ACQUISITION/Acquisition";
import Editorial from "../EDITORIAL/Editorial";
import Acquisition from "../ACQUISITION/Acquisition";
import EmailMarketing from "../ACQUISITION/EmailMarketing";
import WhatsAppMarketing from "../ACQUISITION/WhatsAppMarketing";
import BusinessToken from "../ACQUISITION/BusinessToken";

// ── Tokens — Ahome white/gold/red ─────────────────────────
const T = {
  bg:        "#FFFFFF",
  bgSoft:    "#FAFAF9",
  bgMuted:   "#F5F4F0",
  surface:   "#FFFFFF",
  border:    "#EBEBEB",
  border2:   "#E0E0DC",
  text:      "#0A0A0A",
  textSoft:  "#3C3C3C",
  textMuted: "#8A8A86",
  gold:      "#BF9B30",
  goldLight: "#F0E2C8",
  goldBg:    "#FBF6EE",
  red:       "#E84020",
  sans:      "'DM Sans', sans-serif",
  serif:     "'Sora', sans-serif",
  mono:      "'DM Mono', monospace",
};

const SIDEBAR_FULL = 240;
const SIDEBAR_MINI = 56;
const HEADER_H     = 52;

// ── Tabs ──────────────────────────────────────────────────
type Tab = { key: string; title: string; sub: string; icon: React.ReactNode; group: string };

const TABS: Tab[] = [
  { key: "brands",      title: "Brands",       sub: "Commerce partners",    icon: <Storefront   style={{ fontSize: 15 }} />, group: "Commerce"    },
  { key: "revenue",     title: "Revenue",      sub: "Earnings & payouts",   icon: <EuroSymbol   style={{ fontSize: 15 }} />, group: "Commerce"    },
  { key: "acquisition", title: "Acquisition",  sub: "Onboard new sellers",  icon: <ShoppingBag  size={15} />,                group: "Commerce"    },
  { key: "support",     title: "Support",      sub: "Messages & tickets",   icon: <SupportAgent style={{ fontSize: 15 }} />, group: "Operations"  },
  { key: "editor",      title: "Editorial",    sub: "Content & publishing", icon: <Book         style={{ fontSize: 15 }} />, group: "Operations"  },
  { key: "acquisition",      title: "Acquisition",    sub: "Acquisition", icon: <Book         style={{ fontSize: 15 }} />, group: "Operations"  },
    { key: "email",      title: "EmailMarket",    sub: "Email", icon: <Book         style={{ fontSize: 15 }} />, group: "Operations"  },
    { key: "whatsapp",      title: "Whatsapp",    sub: "Email", icon: <Book         style={{ fontSize: 15 }} />, group: "Operations"  },

];

// ── CSS ───────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FAFAF9; }

  .cm-sidebar  { display: flex !important; }
  .cm-mob-tabs { display: none !important; }

  @media (max-width: 768px) {
    .cm-sidebar  { display: none !important; }
    .cm-main     { margin-left: 0 !important; }
    .cm-mob-tabs { display: flex !important; }
    .cm-desk-crumb { display: none !important; }
  }

  .cm-tab-strip {
    display: flex; overflow-x: auto; gap: 0;
    scrollbar-width: none; -webkit-overflow-scrolling: touch;
  }
  .cm-tab-strip::-webkit-scrollbar { display: none; }

  .cm-mob-tab {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 10px 16px; border: none; background: transparent; cursor: pointer;
    flex-shrink: 0; border-bottom: 2px solid transparent; transition: all .15s ease;
    font-family: 'DM Sans', sans-serif;
  }
  .cm-mob-tab.active { border-bottom-color: #BF9B30; }
  .cm-mob-tab-label {
    font-size: 10px; font-weight: 600; color: #8A8A86;
    white-space: nowrap; letter-spacing: 0.04em; text-transform: uppercase;
  }
  .cm-mob-tab.active .cm-mob-tab-label { color: #BF9B30; }
  .cm-mob-tab-icon { color: #C8C8C4; }
  .cm-mob-tab.active .cm-mob-tab-icon { color: #BF9B30; }

  .cm-nav-item {
    display: flex; align-items: center; gap: 10px; width: 100%;
    padding: 9px 10px; border: none; border-radius: 8px;
    background: transparent; cursor: pointer;
    font-family: 'DM Sans', sans-serif; margin-bottom: 1px;
    transition: background .12s ease; text-align: left;
  }
  .cm-nav-item:hover  { background: #F5F4F0; }
  .cm-nav-item.active { background: #FBF6EE; }

  .cm-nav-scroll { scrollbar-width: none; }
  .cm-nav-scroll::-webkit-scrollbar { display: none; }

  @keyframes cmFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .cm-fade-in { animation: cmFadeIn .3s ease both; }

  @keyframes cmSpin { to { transform: rotate(360deg); } }
`;

// ── Main ──────────────────────────────────────────────────
const CommerceManagementDashboard: React.FC = () => {
  const navigate   = useNavigate();
  const user       = useSelector((s: RootState) => s.auth.user);

  const [active,    setActive]    = useState("brands");
  const [collapsed, setCollapsed] = useState(false);
  const mobTabRef = useRef<HTMLButtonElement>(null);
  const sbW = collapsed ? SIDEBAR_MINI : SIDEBAR_FULL;

  useEffect(() => {
    mobTabRef.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const renderContent = () => {
    switch (active) {
      case "brands":      return <GeneralCommercePartners />;
      case "revenue":     return <Revenue />;
      case "acquisition": return <ElectronicsAcquisition />;
      case "support":     return <CommerceManagementSupport />;
      case "editor":      return <Editorial />;
            case "baquisition":      return <BusinessToken />;

      case "aquisition":      return <Acquisition />;
      case "email":      return <EmailMarketing />;
      case "whatsapp":      return <WhatsAppMarketing />;

      default:            return <GeneralCommercePartners />;
    }
  };

  const groups      = Array.from(new Set(TABS.map(t => t.group)));
  const activeTab   = TABS.find(t => t.key === active);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bgSoft, fontFamily: T.sans, color: T.text }}>
      <style>{CSS}</style>

      {/* ── HEADER ── */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, height: HEADER_H,
        background: T.surface,
        borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px", zIndex: 300,
        boxShadow: "0 1px 0 #EBEBEB",
      }}>
        {/* Left — wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6,
            background: "linear-gradient(135deg,#BF9B30,#E84020)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Storefront style={{ fontSize: 16, color: "#fff" }} />
          </div>
          <div>
            <p style={{ fontFamily: T.serif, fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.1 }}>Commerce</p>
            <p style={{ fontSize: 9, color: T.textMuted, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: T.mono, marginTop: 1 }}>Management console</p>
          </div>
        </div>

        {/* Right — manager badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            background: T.goldBg, border: `1px solid ${T.goldLight}`,
            borderRadius: 6, padding: "5px 12px",
          }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.gold }} />
            <span style={{ fontSize: 10, color: T.gold, fontFamily: T.mono, fontWeight: 600, letterSpacing: "0.06em" }}>
              {user?.firstName || "Manager"}
            </span>
          </div>
        </div>
      </header>

      {/* ── MOBILE TAB BAR ── */}
      <div className="cm-mob-tabs" style={{
        position: "fixed", top: HEADER_H, left: 0, right: 0,
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        zIndex: 200, flexDirection: "column",
      }}>
        <div className="cm-tab-strip">
          {TABS.map(({ key, title, icon }) => (
            <button
              key={key}
              ref={active === key ? mobTabRef : null}
              className={`cm-mob-tab${active === key ? " active" : ""}`}
              onClick={() => setActive(key)}
            >
              <span className="cm-mob-tab-icon">{icon}</span>
              <span className="cm-mob-tab-label">{title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ display: "flex", paddingTop: HEADER_H, flex: 1 }}>

        {/* ── SIDEBAR ── */}
        <aside className="cm-sidebar" style={{
          position: "fixed", top: HEADER_H, left: 0, bottom: 0,
          width: sbW,
          background: T.surface,
          borderRight: `1px solid ${T.border}`,
          flexDirection: "column",
          transition: "width .22s ease",
          zIndex: 200, overflow: "hidden",
        }}>

          {/* Active module block */}
          {!collapsed && (
            <div style={{ padding: "12px 12px 10px", borderBottom: `1px solid ${T.border}` }}>
              <p style={{ fontSize: 9, color: T.textMuted, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: T.mono, marginBottom: 6 }}>
                Active module
              </p>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: T.goldBg, border: `1px solid ${T.goldLight}`,
                borderRadius: 8, padding: "8px 10px",
              }}>
                <span style={{ color: T.gold, flexShrink: 0 }}>{activeTab?.icon}</span>
                <div>
                  <p style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{activeTab?.title}</p>
                  <p style={{ fontSize: 9, color: T.textMuted, marginTop: 1, fontFamily: T.mono }}>{activeTab?.sub}</p>
                </div>
              </div>
            </div>
          )}

          {/* Nav */}
          <nav className="cm-nav-scroll" style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
            {groups.map(group => (
              <div key={group} style={{ marginBottom: 4 }}>
                {!collapsed && (
                  <>
                    <p style={{ fontSize: 9, color: T.textMuted, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: T.mono, padding: "10px 4px 5px" }}>
                      {group}
                    </p>
                    <div style={{ height: 0, borderTop: `1px solid ${T.border}`, marginBottom: 4 }} />
                  </>
                )}
                {TABS.filter(t => t.group === group).map(({ key, title, sub, icon }) => {
                  const isActive = active === key;
                  return (
                    <button
                      key={key}
                      className={`cm-nav-item${isActive ? " active" : ""}`}
                      onClick={() => setActive(key)}
                      title={collapsed ? title : undefined}
                      style={{ justifyContent: collapsed ? "center" : "flex-start", gap: collapsed ? 0 : 10, padding: collapsed ? "11px 0" : "9px 10px" }}
                    >
                      <span style={{ color: isActive ? T.gold : T.textMuted, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 16 }}>
                        {icon}
                      </span>
                      {!collapsed && (
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 12, color: isActive ? T.text : T.textSoft, fontWeight: isActive ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {title}
                          </p>
                          <p style={{ fontSize: 9, color: T.textMuted, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: T.mono }}>
                            {sub}
                          </p>
                        </div>
                      )}
                      {/* Active indicator — gold bar */}
                      {isActive && !collapsed && (
                        <div style={{ width: 2, height: 16, background: T.gold, flexShrink: 0, borderRadius: 2 }} />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Collapse toggle */}
          <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 8px" }}>
            <button
              onClick={() => setCollapsed(p => !p)}
              className="cm-nav-item"
              style={{ justifyContent: collapsed ? "center" : "flex-start", gap: collapsed ? 0 : 8, padding: collapsed ? "10px 0" : "9px 10px" }}
            >
              {collapsed
                ? <ChevronRight style={{ fontSize: 14, color: T.textMuted }} />
                : <>
                    <ChevronLeft style={{ fontSize: 14, color: T.textMuted }} />
                    <span style={{ fontSize: 11, color: T.textMuted, fontFamily: T.mono }}>Collapse</span>
                  </>
              }
            </button>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main className="cm-main" style={{ flex: 1, marginLeft: sbW, transition: "margin-left .22s ease", minWidth: 0, display: "flex", flexDirection: "column" }}>

          {/* Desktop breadcrumb */}
          <div className="cm-desk-crumb" style={{
            height: 40,
            background: T.surface,
            borderBottom: `1px solid ${T.border}`,
            display: "flex", alignItems: "center",
            gap: 8, padding: "0 24px", flexShrink: 0,
          }}>
            <span style={{ fontSize: 10, color: T.textMuted, fontFamily: T.mono }}>Commerce</span>
            <span style={{ fontSize: 10, color: T.border, fontFamily: T.mono }}>/</span>
            <span style={{ fontSize: 10, color: T.gold, fontWeight: 600, fontFamily: T.mono }}>{activeTab?.title}</span>
            <span style={{ fontSize: 10, color: T.border, fontFamily: T.mono }}>/</span>
            <span style={{ fontSize: 10, color: T.textMuted, fontFamily: T.mono }}>{activeTab?.sub}</span>
          </div>

          {/* Page content */}
          <div className="cm-fade-in" style={{ flex: 1, padding: "20px 20px 80px", minWidth: 0 }}>
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CommerceManagementDashboard;